import { usePageLoading } from "@/contexts/page-loading.context";
import {
  authFormClass,
  authPageContainerClass,
} from "@/layout/auth-layout.styles";
import { Button, Separator as Divider, Typography } from "@iluvatar/ui-kit";

import AppleSocialButton from "@/components/AppleLoginButton";
import GoogleSocialButton from "@/components/GoogleLoginButton";
import { useAsyncAction } from "@/hooks/useAsync";
import { waitForAuthReady } from "@/services/auth-session";
import { getAuthRuntime } from "@/services/firebase-auth.runtime";
import { loginErrorMessage } from "@/utilities/login-error-message";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { Form } from "../components/Form";
import DatePicker from "../components/Form/Fields/Date";
import { EmailField } from "../components/Form/Fields/Email";
import { MemoTextField, PasswordField } from "../components/Form/Fields/Input";
import { formErrorHandler } from "../components/Form/helpers";
// validations
import { USER_REGISTRATION_VALIDATION } from "../components/Form/Validation/user-registration";
import { useAuthenticationContext } from "../contexts/authentication/authentication.context";
import { useToastContext } from "../contexts/toast.context";
import requestService from "../services/request.service";

import { FormHelpers } from "@/FormLib";

type RegisterForm = {
  email: string;
  password: string;
  confirmPassword: string;
  birthDate: number | undefined;
  displayName?: string;
};

const RegisterUser: React.FC = () => {
  const { login, ssoLogin, ssoRedirectHandler } = useAuthenticationContext();
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const [disabled, setDisabled] = useState(false);
  usePageLoading(disabled);
  const [ssoError, setSSOError] = useState<string>();

  const startSignup = async (provider: "google.com" | "apple.com") => {
    setDisabled(true);
    setSSOError(undefined);
    try {
      await ssoLogin(provider);
    } catch (error) {
      setSSOError(loginErrorMessage(error));
      setDisabled(false);
    }
  };

  useAsyncAction({
    queryKey: ["signupRedirectHandler"],
    queryFn: async () => {
      setDisabled(true);
      try {
        await waitForAuthReady();
        const { auth, getRedirectResult, browserPopupRedirectResolver } =
          await getAuthRuntime();
        const result = await getRedirectResult(
          auth,
          browserPopupRedirectResolver,
        );
        if (result) {
          await ssoRedirectHandler(result);
          await navigate({ to: "/" });
        }
      } catch (error) {
        setSSOError(loginErrorMessage(error));
      } finally {
        setDisabled(false);
      }
      return null;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const submitForm = async (
    user: RegisterForm,
    helpers: FormHelpers<RegisterForm>,
  ) => {
    try {
      setDisabled(true);
      await requestService.request("/api/register", {
        method: "post",
        body: JSON.stringify({
          user,
        }),
      });
      const res = await login(user.email, user.password);
      addToast("Account Created");
      await helpers.resetForm();

      if (res) {
        await navigate({
          to: "/user/$userId/edit",
          params: {
            userId: res[0].uid,
          },
        });
      } else {
        await navigate({ to: "/login" });
      }
    } catch (error) {
      formErrorHandler(error, helpers);
    } finally {
      setDisabled(false);
    }
  };

  return (
    <div className={`${authPageContainerClass} mx-auto`}>
      <Typography
        variant="h5"
        className={[
          "auth-title",
          "[color:rgb(var(--uik-subtle))] [margin-top:24px]",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        Create an Account
      </Typography>

      <div className="my-6 flex w-full max-w-[420px] flex-col gap-4 px-6">
        <GoogleSocialButton
          disabled={disabled}
          onClick={() => void startSignup("google.com")}
        >
          Sign up with Google
        </GoogleSocialButton>
        <AppleSocialButton
          disabled={disabled}
          onClick={() => void startSignup("apple.com")}
        >
          Sign up with Apple
        </AppleSocialButton>
        {ssoError && (
          <Typography role="alert" className="[color:rgb(var(--uik-danger))]">
            {ssoError}
          </Typography>
        )}
      </div>
      <Divider className="w-full max-w-[420px]">or sign up with email</Divider>
      <Form<RegisterForm>
        id="register"
        outerStyle={{ alignItems: "center" }}
        renderAfter
        disabled={disabled}
        onSubmit={submitForm}
        validationSchema={USER_REGISTRATION_VALIDATION}
        initialValues={{
          email: "",
          password: "",
          confirmPassword: "",
          birthDate: undefined,
          displayName: "",
        }}
        className={`${authFormClass} max-w-[420px]`}
      >
        <MemoTextField
          required
          label="Display Name"
          name="displayName"
          description="What name would you like to go by?"
          className="auth-form-element"
        />
        <EmailField
          required
          label="Email"
          name="email"
          className="auth-form-element"
        />
        <DatePicker
          required
          outputNumber
          label="Birth Date"
          name="birthDate"
          className="auth-form-element"
        />
        <PasswordField
          required
          label="Password"
          name="password"
          description="The super secret key you definitely don't use everywhere..."
          className="auth-form-element"
        />
        <PasswordField
          required
          label="Confirm Password"
          name="confirmPassword"
          className="auth-form-element"
        />
        <Button
          form="register"
          variant="outline"
          type="submit"
          disabled={disabled}
          className={[
            "auth-form-element auth-button bg-[var(--color-background-paper)]",
            "w-full [margin-top:24px]",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          Register
        </Button>
      </Form>
      <Divider />
      <Link
        to="/login"
        className="auth-secondary-link flex f-c-c items-center justify-center my-3"
        style={{ color: "var(--color-text--secondary)" }}
      >
        Back to login
      </Link>
      {/* <Link
          to="/password-reset"
          style={{ color: "var(--color-text--secondary)" }}
        >
          Forgot password?
        </Link> */}
    </div>
  );
};

export const Route = createFileRoute("/register")({
  component: RegisterUser,
});
